import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useAuth } from '@/stores/auth'
import ParishSelector from '@/components/parish/ParishSelector'
import { authApi } from '@/api/endpoints/auth'

interface Props {
  onParishCreate?: () => void
  onParishSwitched?: () => Promise<void> | void
  parishListRefreshKey?: number
}

export default function AppHeader({ onParishCreate, onParishSwitched, parishListRefreshKey = 0 }: Props) {
  const { user, logout, updateUser } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  if (!user) return null

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Title */}
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="Catechism Cloud Logo" 
              className="h-6 w-6 object-contain"
            />
            <Link to="/dashboard" className="text-2xl font-bold text-gray-900 hover:opacity-80 transition-opacity">
              <span className="font-display">Catechism Cloud</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {user.role === 'PRIMARY_ADMIN' && (
              <ParishSelector
                canCreate
                onCreateNew={onParishCreate || (() => {})}
                onSwitched={async () => {
                  const me = await authApi.getMe()
                  updateUser(me)
                  await onParishSwitched?.()
                }}
                refreshTrigger={parishListRefreshKey}
              />
            )}
            <Link to="/settings" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
              Settings
            </Link>
            <span className="text-sm font-bold text-amber-900">
              {user.first_name} {user.last_name}
            </span>
            <button
              onClick={logout}
              className="btn-secondary text-sm focus-ring"
            >
              Logout
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus-ring"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              className="md:hidden border-t border-gray-200 py-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex flex-col space-y-3">
                {user.role === 'PRIMARY_ADMIN' && (
                  <div className="px-2">
                    <ParishSelector
                      canCreate
                      onCreateNew={() => {
                        setMobileMenuOpen(false)
                        onParishCreate?.()
                      }}
                      onSwitched={async () => {
                        const me = await authApi.getMe()
                        updateUser(me)
                        await onParishSwitched?.()
                        setMobileMenuOpen(false)
                      }}
                      refreshTrigger={parishListRefreshKey}
                    />
                  </div>
                )}
                <Link
                  to="/settings"
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Settings
                </Link>
                <div className="px-4 py-2 text-sm border-t border-gray-100 pt-3">
                  <span className="font-bold text-amber-900">{user.first_name} {user.last_name}</span>
                  <span className="text-gray-600"> ({user.role})</span>
                </div>
                <button
                  onClick={() => {
                    setMobileMenuOpen(false)
                    logout()
                  }}
                  className="mx-4 btn-secondary text-sm focus-ring"
                >
                  Logout
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

